import { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useQuery, useMutation, useQueryClient } from "react-query";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WorkIcon from "@mui/icons-material/Work";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";

import AuthContext from "../../contexts/AuthContext";
import GridItem from "./GridItem";
import FieldHeader from "./FieldHeader";
import CardText from "./CardText";
import ViewDocumentCard from "./ViewDocumentCard";
import ViewOtherDocuments from "./ViewOtherDocuments";
import ApprovalModal from "./Initiator/ApprovalModal";
import RejectModal from "./Initiator/RejectModal";
import { REQUESTREJECTIONREASONS } from "../../utils/constants";
import {
	getRequest,
	approveInititorKycUpdateRequest,
	rejectInititorKycUpdateRequest,
} from "../../services/admin";
import { newDateFormatter } from "../../utils/helpers";

const ViewIndividualRequestUpdate = () => {
	const [openApproveModal, setOpenApproveModal] = useState(false);
	const [openRejectModal, setOpenRejectModal] = useState(false);
	const [checkedState, setCheckedState] = useState(
		new Array(REQUESTREJECTIONREASONS.length).fill(false)
	);
	const [otherReason, setotherReason] = useState("");

	const queryClient = useQueryClient();

	const { user } = useContext(AuthContext);

	const { id } = useParams();
	const {
		data: request = {},
		isLoading: requestLoading,
		isError: requestError,
		error: requestErrorMessage,
	} = useQuery(["get-request", id], async () => getRequest(id), {
		select: (data) => data.data,
		staleTime: 4 * 60 * 1000,
	});

	useEffect(() => {
		if (requestError) {
			if (requestErrorMessage.response) {
				toast.error(requestErrorMessage.response.data?.message);
			} else if (requestErrorMessage.request) {
				toast.error("Internal Server Error");
			} else {
				toast.error("Error", requestErrorMessage.message);
			}
		}
	}, [requestError, requestErrorMessage]);

	const { mutate: approveUpdateRequest, isLoading: approveLoading } =
		useMutation("approve-kycUpdate", approveInititorKycUpdateRequest, {
			onSuccess: () => {
				setOpenApproveModal(false);
				toast.success("KYC Update Request Approved Successfully");
			},
			onError: (errors) => {
				if (errors.response) {
					toast.error(errors.response.data.message);
				} else if (errors.request) {
					toast.error("Internal Server Error");
				} else {
					toast.error("Error", errors.message);
				}
			},
			onSettled: () => {
				queryClient.invalidateQueries("get-request");
			},
		});

	const { mutate: rejectUpdateRequest, isLoading: rejectLoading } = useMutation(
		"reject-kycUpdate",
		rejectInititorKycUpdateRequest,
		{
			onSuccess: () => {
				setOpenRejectModal(false);
				toast.success("KYC Update Request Rejected Successfully");
			},
			onError: (errors) => {
				if (errors.response) {
					toast.error(errors.response.data.message);
				} else if (errors.request) {
					toast.error("Internal Server Error");
				} else {
					toast.error("Error", errors.message);
				}
			},
			onSettled: () => {
				queryClient.invalidateQueries("get-request");
			},
		}
	);

	const handleRequestApprove = async () => {
		const payload = {
			id,
			initiator: user?.fullName,
		};
		await approveUpdateRequest(payload);
	};

	const handleRejectRequest = async () => {
		if (!checkedState.some((item) => item)) {
			toast.error("Please choose a rejection reason");
			return;
		}
		const rejected = checkedState
			.map((item, index) => (item ? REQUESTREJECTIONREASONS[index] : false))
			.filter((item) => item !== false);
		const index = rejected.indexOf("Specify any other reason");
		const rejectedReasons = rejected;
		if (index !== -1) {
			rejectedReasons[index] = otherReason;
			if (!otherReason) {
				toast.error(
					"Kindly type in other reason for rejection or uncheck other reason checkbox"
				);
				return;
			}
		}
		const payload = {
			id,
			rejectReasons: rejectedReasons,
			initiator: user?.fullName,
		};

		await rejectUpdateRequest(payload);
	};

	const handleOnChange = (position) => {
		const updatedCheckedState = checkedState.map((item, index) =>
			index === position ? !item : item
		);
		setCheckedState(updatedCheckedState);
	};

	return (
		<Box
			sx={{
				padding: {
					md: "3rem",
					sm: "1.5rem",
					xs: "1rem",
				},
				pt: "6rem !important",
			}}
		>
			{requestLoading && (
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						width: "100%",
						height: "100vh",
						mt: "-6rem",
					}}
				>
					<CircularProgress />
				</Box>
			)}
			{!requestLoading && (
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
					}}
				>
					<Box sx={{ width: { xs: "100%", md: "75%", lg: "67%" } }}>
						<Box
							sx={{
								padding: { md: "1.5rem", xs: "1rem", textAlign: "center" },
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								borderRadius: "0.5rem",
								backgroundColor: "#f7fafc",
							}}
						>
							<Box
								sx={{
									height: "8rem",
									width: "8rem",
									display: "flex",
									marginTop: { md: "-5.5rem", xs: "-5rem" },
								}}
							>
								<img
									src={request?.passportOne}
									alt=""
									height="100%"
									width="100%"
									style={{ objectFit: "cover", borderRadius: "50%" }}
								/>
							</Box>
							<Typography
								variant="h5"
								sx={{ mt: "1rem", fontWeight: "bold" }}
								gutterBottom
								component="div"
								color="primary"
							>
								{request.lastname
									? `${request.lastname || ""}, ${request.firstname || ""} ${
											request.middlename || ""
									  }`
									: request.lastname}
							</Typography>

							<Typography
								variant="body1"
								sx={{ fontWeight: "bold", color: "#a0aec0" }}
								component="div"
							>
								<LocationOnIcon sx={{ mb: "-0.4rem" }} />{" "}
								{request.addressLineOne || ""} {request.addressLineTwo || ""}
							</Typography>
							<Typography
								variant="body1"
								sx={{ fontWeight: "bold", color: "#a0aec0" }}
								gutterBottom
								component="div"
							>
								{request.city || ""} {request.state || ""},{" "}
								{request.country || ""}
							</Typography>

							<Typography
								variant="body1"
								sx={{ color: "#4a5568" }}
								component="div"
							>
								<WorkIcon sx={{ mb: "-0.4rem", color: "#a0aec0" }} />{" "}
								{request.occupation || ""} {request.employer || ""}
							</Typography>
							<Typography
								variant="body1"
								sx={{ color: "#4a5568" }}
								gutterBottom
								component="div"
							>
								{request.employerAddress || ""}
							</Typography>
						</Box>

						<Divider
							style={{
								marginTop: "2rem",
								marginBottom: "2rem",
							}}
						/>

						<Box>
							<FieldHeader header="Basic Details" subHeader="" />
							<Box
								sx={{
									padding: { md: "1.5rem", xs: "1rem" },
									borderRadius: "0.5rem",
									backgroundColor: "#f7fafc",
								}}
							>
								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<Grid item xs={12}>
											<CardText
												header={request.accountType}
												subHeader="account type"
											/>
										</Grid>
									</Grid>
								</Box>

								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<GridItem>
											<CardText
												header={request.title?.toUpperCase()}
												subHeader="title"
											/>
										</GridItem>
										<GridItem>
											<CardText
												header={request.firstname?.toUpperCase()}
												subHeader="first name"
											/>
										</GridItem>
									</Grid>
								</Box>

								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<GridItem>
											<CardText
												header={request.middlename?.toUpperCase()}
												subHeader="middle name"
											/>
										</GridItem>
										<GridItem>
											<CardText
												header={request.lastname?.toUpperCase()}
												subHeader="last name"
											/>
										</GridItem>
									</Grid>
								</Box>

								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<GridItem>
											<CardText
												header={
													request.gender === "M"
														? "male".toUpperCase()
														: "female".toUpperCase()
												}
												subHeader="gender"
											/>
										</GridItem>
										<GridItem>
											<CardText
												header={newDateFormatter(request.dateOfBirth)}
												subHeader="date of birth"
											/>
										</GridItem>
									</Grid>
								</Box>

								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<Grid item xs={12}>
											<CardText
												header={request.email?.toUpperCase()}
												subHeader="email"
											/>
										</Grid>
									</Grid>
								</Box>

								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<GridItem>
											<CardText header={request.phone} subHeader="phone" />
										</GridItem>
										<GridItem>
											<CardText
												header={request.secondaryPhone}
												subHeader="secondary phone"
											/>
										</GridItem>
									</Grid>
								</Box>

								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<Grid item xs={12}>
											<CardText
												header={request.nationality?.toUpperCase()}
												subHeader="nationality"
											/>
										</Grid>
									</Grid>
								</Box>
							</Box>
						</Box>

						<Divider
							style={{
								marginTop: "2rem",
								marginBottom: "2rem",
							}}
						/>

						<Box>
							<FieldHeader header="Address Info" subHeader="" />
							<Box
								sx={{
									padding: { md: "1.5rem", xs: "1rem" },
									borderRadius: "0.5rem",
									backgroundColor: "#f7fafc",
								}}
							>
								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<Grid item xs={12}>
											<CardText
												header={request.addressLineOne}
												subHeader="address line 1"
											/>
										</Grid>
									</Grid>
								</Box>

								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<Grid item xs={12}>
											<CardText
												header={request.addressLineTwo}
												subHeader="address line 2"
											/>
										</Grid>
									</Grid>
								</Box>

								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<GridItem>
											<CardText
												header={request.city}
												subHeader="address city"
											/>
										</GridItem>
										<GridItem>
											<CardText
												header={request.state}
												subHeader="address state"
											/>
										</GridItem>
									</Grid>
								</Box>

								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<Grid item xs={12}>
											<CardText
												header={request.country}
												subHeader="address country"
											/>
										</Grid>
									</Grid>
								</Box>
							</Box>
						</Box>

						<Divider
							style={{
								marginTop: "2rem",
								marginBottom: "2rem",
							}}
						/>

						<Box>
							<FieldHeader header="Work" subHeader="" />
							<Box
								sx={{
									padding: { md: "1.5rem", xs: "1rem" },
									borderRadius: "0.5rem",
									backgroundColor: "#f7fafc",
								}}
							>
								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<GridItem>
											<CardText
												header={request.occupation}
												subHeader="occupation"
											/>
										</GridItem>
										<GridItem>
											<CardText
												header={request.employer}
												subHeader="employer"
											/>
										</GridItem>
									</Grid>
								</Box>

								<Grid container spacing={2}>
									<Grid item xs={12}>
										<CardText
											header={request.employerAddress}
											subHeader="employer address"
										/>
									</Grid>
								</Grid>
							</Box>
						</Box>

						<Divider
							style={{
								marginTop: "2rem",
								marginBottom: "2rem",
							}}
						/>

						<Box>
							<FieldHeader header="Identification" subHeader="" />
							<Box
								sx={{
									padding: { md: "1.5rem", xs: "1rem" },
									borderRadius: "0.5rem",
									backgroundColor: "#f7fafc",
								}}
							>
								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<GridItem>
											<CardText
												header={request.identityTypeOne}
												subHeader="Means of Identification"
											/>
										</GridItem>
										{Boolean(request.enterIdentityTypeOne) && (
											<GridItem>
												<CardText
													header={request.enterIdentityTypeOne}
													subHeader="Identity Type"
												/>
											</GridItem>
										)}
									</Grid>
								</Box>

								<Box>
									<Grid container spacing={2}>
										<GridItem>
											<CardText
												header={request.identityNumberOne}
												subHeader="ID Number"
											/>
										</GridItem>
										<GridItem>
											<CardText
												header={newDateFormatter(request.identityExpiryDateOne)}
												subHeader="Expiry Date"
											/>
										</GridItem>
									</Grid>
								</Box>
							</Box>
						</Box>

						<Divider
							style={{
								marginTop: "2rem",
								marginBottom: "2rem",
							}}
						/>

						<Box>
							<FieldHeader
								header="Documents"
								subHeader="Uploaded Signature mandate, Passport photograph, and Means of identification."
							/>
							<Box
								sx={{
									padding: { md: "1.5rem", xs: "1rem" },
									borderRadius: "0.5rem",
									backgroundColor: "#f7fafc",
								}}
							>
								<ViewDocumentCard
									image={request.passportOne}
									title="Passport"
								/>
								<ViewDocumentCard
									image={request.signatureOne}
									title="Signature Mandate"
								/>
								<ViewDocumentCard
									image={request.identificationOne}
									title="Identification Document"
								/>
							</Box>
						</Box>

						<Divider
							style={{
								marginTop: "2rem",
								marginBottom: "2rem",
							}}
						/>

						<Box>
							<FieldHeader header="Other Documents" subHeader="" />
							<Box
								sx={{
									padding: { md: "1.5rem", xs: "1rem" },
									borderRadius: "0.5rem",
									backgroundColor: "#f7fafc",
								}}
							>
								{request.otherDocuments ? (
									<ViewOtherDocuments
										image={request.otherDocuments}
										title="Other Documents"
									/>
								) : (
									"No other document uploaded."
								)}
							</Box>
						</Box>

						<Divider
							style={{
								marginTop: "2rem",
								marginBottom: "2rem",
							}}
						/>

						<Box>
							<FieldHeader header="Approving Officers" subHeader="" />
							<Box
								sx={{
									padding: { md: "1.5rem", xs: "1rem" },
									borderRadius: "0.5rem",
									backgroundColor: "#f7fafc",
								}}
							>
								<Typography
									variant="body1"
									sx={{
										color: "#718096",
										textTransform: "uppercase",
										fontWeight: 500,
										mb: "1rem",
									}}
								>
									001. APPROVING OFFICER
								</Typography>
								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<GridItem>
											<CardText header={request.initiator} subHeader="Name" />
										</GridItem>
									</Grid>
								</Box>

								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<GridItem>
											<CardText
												header={newDateFormatter(request.approvalDateOne)}
												subHeader="Approval Date"
											/>
										</GridItem>
										<GridItem>
											<CardText
												header={request.status}
												subHeader="Approval Status"
											/>
										</GridItem>
									</Grid>
								</Box>

								<Divider
									style={{
										marginTop: "2rem",
										marginBottom: "2rem",
									}}
								/>

								<Typography
									variant="body1"
									sx={{
										color: "#718096",
										textTransform: "uppercase",
										fontWeight: 500,
										mb: "1rem",
									}}
								>
									002. APPROVING OFFICER
								</Typography>
								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<GridItem>
											<CardText header={request.authorizer} subHeader="Name" />
										</GridItem>
									</Grid>
								</Box>

								<Box sx={{ mb: "1rem" }}>
									<Grid container spacing={2}>
										<GridItem>
											<CardText
												header={newDateFormatter(request.approvalDateTwo)}
												subHeader="Approval Date"
											/>
										</GridItem>
										<GridItem>
											<CardText
												header={request.status}
												subHeader="Approval Status"
											/>
										</GridItem>
									</Grid>
								</Box>
							</Box>
						</Box>

						<Divider
							style={{
								marginTop: "2rem",
								marginBottom: "2rem",
							}}
						/>

						<Box>
							<FieldHeader header="Approval Status" subHeader="" />
							<Box
								sx={{
									padding: { md: "1.5rem", xs: "1rem" },
									borderRadius: "0.5rem",
									boxShadow:
										"0 1px 3px 0 rgb(0 0 0 / 10%), 0 1px 2px 0 rgb(0 0 0 / 6%)",
								}}
							>
								<Typography
									variant="body2"
									sx={{
										fontWeight: 400,
										mb: "1rem",
									}}
									gutterBottom
								>
									These details requires both approvals.
								</Typography>
								<Box sx={{ display: "flex", justifyContent: "space-between" }}>
									<Button
										variant="contained"
										color="success"
										onClick={() => setOpenApproveModal(true)}
									>
										approve update
									</Button>
									<Button
										variant="contained"
										color="error"
										onClick={() => setOpenRejectModal(true)}
									>
										reject update
									</Button>
								</Box>
							</Box>
						</Box>
					</Box>
				</Box>
			)}
			<ApprovalModal
				open={openApproveModal}
				handleClose={() => setOpenApproveModal(false)}
				title="Approve KYC Update Request"
				subtitle=""
				handleRequestApprove={handleRequestApprove}
				approveLoading={approveLoading}
			/>
			<RejectModal
				open={openRejectModal}
				handleClose={() => setOpenRejectModal(false)}
				title="Reject KYC Update Request"
				subtitle=""
				handleRejectRequest={handleRejectRequest}
				rejectLoading={rejectLoading}
				REQUESTREJECTIONREASONS={REQUESTREJECTIONREASONS}
				otherReason={otherReason}
				setotherReason={setotherReason}
				checkedState={checkedState}
				handleOnChange={handleOnChange}
			/>
		</Box>
	);
};

export default ViewIndividualRequestUpdate;
